import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import pluralize from "pluralize";
import {
    ActivateFeatureButton,
    BusinessPortalHeader,
    DataTable,
    DataTableCell,
    DataTableContainer,
    DataTableHeaderCell,
    PortalPageShell,
    Toast,
    DangerButton,
    SmallActionButton,
    PlaylistThumb,
    PortalCaption,
    PortalKicker,
    PortalPanelHeader,
    PortalSearchInput,
    PortalSectionTitle,
    PortalSelect,
    PortalSurface,
    PortalTableActions,
    PortalTableControls,
    UpperControls
} from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import HeaderCards from "../BodyAndMind/components/HeaderCards";
import { PageLoadingContainer } from "src/scenes/BodyAndMind/components/PageLoadingComponent";
import ContentIcon from "src/img/playlists icon black@2x.png";
import SpotifyLogo from "src/img/spotify@2x.png";
import AppleMusicLogo from "src/img/apple music@2x.png";
import {
    deletePlaylist,
    getPlaylists,
    updateActivePlaylists
} from "./api";
import PlaylistFormModal from "./components/PlaylistFormModal";
import ManagePlaylistsModal from "./components/ManagePlaylistsModal";
import { PlatformPill, getErrorMessage, getVisibilityText } from "./components/shared";
import { customNames } from "src/utils/helpers";

const Surface = styled.div`
    max-width: 98.625rem;
    margin-bottom: 2rem;
`;

const EmptyState = styled.div`
    padding: 1rem 1.5rem 1.25rem;
    text-align: center;
    color: var(--calendar-text-muted, #6a7387);
    background: var(--calendar-surface-secondary, #f7f9fd);
    border-top: 1px solid var(--calendar-border, #d8deec);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 600;
`;

export default function Playlists() {
    const [playlists, setPlaylists] = useState([]);
    const [companyPlaylistCap, setCompanyPlaylistCap] = useState(0);
    const [isFetching, setIsFetching] = useState(false);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selectedUuids, setSelectedUuids] = useState([]);
    const [showManageModal, setShowManageModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);

    const loadPlaylists = async () => {
        setIsFetching(true);
        try {
            const response = await getPlaylists();
            setPlaylists(response.playlists || []);
            setCompanyPlaylistCap(response.companyPlaylistCap || 0);
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to fetch playlists."));
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        loadPlaylists();
    }, []);

    useEffect(() => {
        setSelectedUuids(prev => prev.filter(uuid => playlists.some(playlist => playlist.uuid === uuid && playlist.isCompanyList)));
    }, [playlists]);

    const filteredPlaylists = useMemo(() => {
        const term = search.trim().toLowerCase();

        return playlists.filter(playlist => {
            const matchesFilter = filter === "all"
                || (filter === "company" && playlist.isCompanyList)
                || (filter === "default" && !playlist.isCompanyList);

            const haystack = `${playlist.name} ${playlist.description}`.toLowerCase();
            const matchesSearch = !term || haystack.includes(term);

            return matchesFilter && matchesSearch;
        });
    }, [filter, playlists, search]);

    const selectableFilteredPlaylists = useMemo(
        () => filteredPlaylists.filter(playlist => playlist.isCompanyList),
        [filteredPlaylists]
    );

    const companyPlaylists = useMemo(() => playlists.filter(playlist => playlist.isCompanyList), [playlists]);

    const canCreatePlaylist = companyPlaylistCap === 0 || companyPlaylists.length < companyPlaylistCap;

    const allSelectableRowsSelected = selectableFilteredPlaylists.length > 0
        && selectableFilteredPlaylists.every(playlist => selectedUuids.includes(playlist.uuid));

    const handleToggleAll = () => {
        const selectableIds = selectableFilteredPlaylists.map(playlist => playlist.uuid);
        if (!selectableIds.length) return;

        const allSelected = selectableIds.every(uuid => selectedUuids.includes(uuid));
        if (allSelected) {
            setSelectedUuids(prev => prev.filter(uuid => !selectableIds.includes(uuid)));
        } else {
            setSelectedUuids(prev => Array.from(new Set([...prev, ...selectableIds])));
        }
    };

    const handleToggleRow = (playlist) => {
        if (!playlist.isCompanyList) return;
        setSelectedUuids(prev => prev.includes(playlist.uuid)
            ? prev.filter(uuid => uuid !== playlist.uuid)
            : [...prev, playlist.uuid]
        );
    };

    const openCreateModal = () => {
        if (!canCreatePlaylist) {
            Toast.error(`You have reached the company playlist limit of ${companyPlaylistCap}.`);
            return;
        }
        setSelectedPlaylist(null);
        setShowFormModal(true);
    };

    const openEditModal = (playlist) => {
        if (!playlist.isCompanyList) {
            Toast.error("Default playlists cannot be edited.");
            return;
        }
        setSelectedPlaylist(playlist);
        setShowFormModal(true);
    };

    const handleDeleteSelected = async () => {
        if (!selectedUuids.length) return;

        try {
            await Promise.all(selectedUuids.map(uuid => deletePlaylist(uuid)));
            Toast.success("Selected playlists deleted.");
            setSelectedUuids([]);
            await loadPlaylists();
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to delete playlists."));
        }
    };

    const handleSaveActive = async (activeIds) => {
        try {
            await updateActivePlaylists(activeIds);
            Toast.success("Playlist visibility updated.");
            setShowManageModal(false);
            await loadPlaylists();
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to update playlist visibility."));
        }
    };

    return (
        <PortalPageShell>
            <BusinessPortalHeader
                title="Playlists"
                description={`Create and manage custom playlists that appear in your Me Business app. Add your own image, title, and description, then link a playlist from Spotify or Apple Music. Choose who can see each playlist — ${pluralize(customNames.employees.toLowerCase())}, ${pluralize(customNames.clients.toLowerCase())}, or both — so the right content reaches the right audience.`}
            />

            <PortalSurface padding="1rem" style={{ maxWidth: "98.625rem", marginBottom: "1rem" }}>
                <PortalPanelHeader withDivider>
                    <div>
                        <PortalKicker>Content Library</PortalKicker>
                        <PortalSectionTitle>Playlist Overview</PortalSectionTitle>
                        <PortalCaption>
                            Review available playlists and open the visibility manager from this dedicated section.
                        </PortalCaption>
                    </div>
                </PortalPanelHeader>
                <HeaderCards
                    icon={ContentIcon}
                    title="Playlists"
                    quantity={playlists.length}
                    toggleFunction={() => setShowManageModal(true)}
                    width="199px"
                />
            </PortalSurface>

            <Surface>
                <PageLoadingContainer isFetching={isFetching}>
                    <UpperControls>
                        <PortalTableControls>
                            <PortalSearchInput
                                width="22.5rem"
                                placeholder="Search Playlist"
                                type="text"
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                            />
                            <PortalSelect value={filter} onChange={event => setFilter(event.target.value)} width="14rem">
                                <option value="all">All Playlists</option>
                                <option value="company">Custom Playlists</option>
                                <option value="default">Me Business Playlists</option>
                            </PortalSelect>
                            {selectedUuids.length > 0 && (
                                <DangerButton onClick={handleDeleteSelected}>
                                    Delete Playlist
                                </DangerButton>
                            )}
                        </PortalTableControls>
                        <PortalTableActions>
                            <ActivateFeatureButton type="button" width="145px" onClick={openCreateModal}>
                                Create Playlist
                            </ActivateFeatureButton>
                        </PortalTableActions>
                    </UpperControls>

                    <DataTableContainer
                        tableMinWidth="960px"
                        style={{ maxWidth: "98.625rem", borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                    >
                        <DataTable minWidth="960px">
                            <thead>
                                <tr>
                                    <DataTableHeaderCell textAlign="center">
                                        <Checkbox checked={allSelectableRowsSelected} onClickCallback={handleToggleAll} />
                                    </DataTableHeaderCell>
                                    <DataTableHeaderCell textAlign="center">Image</DataTableHeaderCell>
                                    <DataTableHeaderCell>Title</DataTableHeaderCell>
                                    <DataTableHeaderCell>Description</DataTableHeaderCell>
                                    <DataTableHeaderCell>Visible to</DataTableHeaderCell>
                                    <DataTableHeaderCell textAlign="center">Availability</DataTableHeaderCell>
                                    <DataTableHeaderCell textAlign="center">View/Edit</DataTableHeaderCell>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPlaylists.length ? filteredPlaylists.map(playlist => {
                                    const isSelected = selectedUuids.includes(playlist.uuid);
                                    return (
                                        <tr key={playlist.uuid}>
                                            <DataTableCell textAlign="center">
                                                {playlist.isCompanyList ? (
                                                    <Checkbox checked={isSelected} onClickCallback={() => handleToggleRow(playlist)} />
                                                ) : null}
                                            </DataTableCell>
                                            <DataTableCell textAlign="center">
                                                <div style={{ display: "flex", justifyContent: "center" }}>
                                                    <PlaylistThumb>{playlist.imageUrl && <img src={playlist.imageUrl} alt={playlist.name} />}</PlaylistThumb>
                                                </div>
                                            </DataTableCell>
                                            <DataTableCell>{playlist.name}</DataTableCell>
                                            <DataTableCell whiteSpace="normal">{playlist.description}</DataTableCell>
                                            <DataTableCell>{getVisibilityText(playlist)}</DataTableCell>
                                            <DataTableCell textAlign="center">
                                                <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                                                    { playlist.spotifyUri &&
                                                        <PlatformPill src={SpotifyLogo} />
                                                    }
                                                    { playlist.appleMusicUrl &&
                                                        <PlatformPill src={AppleMusicLogo} />
                                                    }
                                                </div>
                                            </DataTableCell>
                                            <DataTableCell textAlign="center">
                                                {playlist.isCompanyList ? (
                                                    <SmallActionButton type="button" onClick={() => openEditModal(playlist)}>
                                                        View/Edit
                                                    </SmallActionButton>
                                                ) : (
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--calendar-text, #171A22)" }}>
                                                        Me Business Playlist
                                                    </div>
                                                )}
                                            </DataTableCell>
                                        </tr>
                                    );
                                }) : Array.from({ length: 6 }).map((_, index) => (
                                    <tr key={`empty-${index}`} className="placeholder-row">
                                        <DataTableCell $empty></DataTableCell>
                                        <DataTableCell $empty></DataTableCell>
                                        <DataTableCell $empty></DataTableCell>
                                        <DataTableCell $empty></DataTableCell>
                                        <DataTableCell $empty></DataTableCell>
                                        <DataTableCell $empty></DataTableCell>
                                        <DataTableCell $empty></DataTableCell>
                                    </tr>
                                ))}
                            </tbody>
                        </DataTable>
                        {!filteredPlaylists.length && <EmptyState>No playlists match the current search and filter.</EmptyState>}
                    </DataTableContainer>
                </PageLoadingContainer>
            </Surface>

            {showManageModal && (
                <ManagePlaylistsModal
                    playlists={playlists}
                    onClose={() => setShowManageModal(false)}
                    onCreate={() => {
                        setShowManageModal(false);
                        openCreateModal();
                    }}
                    onEdit={(playlist) => {
                        setShowManageModal(false);
                        openEditModal(playlist);
                    }}
                    onSave={handleSaveActive}
                />
            )}

            {showFormModal && (
                <PlaylistFormModal
                    playlist={selectedPlaylist}
                    onClose={() => setShowFormModal(false)}
                    onSaved={async () => {
                        setShowFormModal(false);
                        setSelectedPlaylist(null);
                        await loadPlaylists();
                    }}
                    onDeleted={async () => {
                        setShowFormModal(false);
                        setSelectedPlaylist(null);
                        setSelectedUuids([]);
                        await loadPlaylists();
                    }}
                />
            )}
        </PortalPageShell>
    );
}
